
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from './useAuth';

export interface DataExportRequest {
  id: string;
  user_id: string;
  request_type: 'export' | 'deletion' | 'anonymization';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  file_url?: string;
  expires_at?: string;
  processed_by?: string;
  processed_at?: string;
  error_message?: string;
  created_at: string;
}

export const useDataManagement = () => {
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState<DataExportRequest[]>([]);
  const { toast } = useToast();
  const { user } = useAuth();

  const createDataRequest = async (requestType: 'export' | 'deletion' | 'anonymization') => {
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to make data requests",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('data_export_requests')
        .insert({
          user_id: user.id,
          request_type: requestType,
          status: 'pending'
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Request Submitted",
        description: `Your ${requestType} request has been submitted and will be processed soon.`,
      });

      fetchUserRequests();
      return data;
    } catch (error: any) {
      console.error('Error creating data request:', error);
      toast({
        title: "Error",
        description: `Failed to submit ${requestType} request: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUserRequests = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('data_export_requests')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      // Type assertion to ensure compatibility with our interface
      setRequests((data || []) as DataExportRequest[]);
    } catch (error: any) {
      console.error('Error fetching user requests:', error);
      toast({
        title: "Error",
        description: "Failed to load your data requests",
        variant: "destructive",
      });
    }
  };

  const fetchAllRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('data_export_requests')
        .select(`
          *,
          profiles:user_id (username, full_name),
          processed_by_profile:processed_by (username, full_name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      // Type assertion to ensure compatibility with our interface
      setRequests((data || []) as DataExportRequest[]);
    } catch (error: any) {
      console.error('Error fetching all requests:', error);
      toast({
        title: "Error",
        description: "Failed to load data requests",
        variant: "destructive",
      });
    }
  };

  const processDataRequest = async (requestId: string) => {
    setLoading(true);
    try {
      const { error } = await supabase.rpc('process_data_export_request', {
        p_request_id: requestId
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Data request has been processed successfully",
      });

      fetchAllRequests();
    } catch (error: any) {
      console.error('Error processing data request:', error);
      toast({
        title: "Error",
        description: `Failed to process data request: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    requests,
    createDataRequest,
    fetchUserRequests,
    fetchAllRequests,
    processDataRequest,
  };
};
